import React from 'react';

import { TooltipCustom } from 'core/chatbot/components/atoms/legacyAtoms';
import { Icon, Loading } from 'ui';
import StatCard from '../../../../components/atoms/StatCard/StatCard';
import type { RadarAlert } from '../../../../types/radar';
import { getInitials } from '../../../../utils/formatUtils';
import RadarTypeTag from '../../RadarTypeTag/RadarTypeTag';
import SeverityBadge from '../../SeverityIndicator/SeverityBadge';
import './_group-learning-block-content.scss';
import useGroupLearningBlockContent from './useGroupLearningBlockContent';

type GroupLearningBlockContentProps = {
  alert: RadarAlert;
  isMockMode?: boolean;
};

function GroupLearningBlockContent({ alert, isMockMode }: GroupLearningBlockContentProps): JSX.Element {
  const { detail, error, handleToggleStudents, isLoading, isStudentsExpanded, locationItems, studentsBySeverity, t } = useGroupLearningBlockContent({
    annotationId: alert.id,
    isMockMode,
  });

  if (isLoading) {
    return (
      <div className="alert-detail-drawer__loading">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert-detail-drawer__error">
        <p>{t('chatbot:ai_analytics_student_conversation_error')}</p>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="alert-detail-drawer__empty">
        <p>{t('chatbot:ai_analytics_student_conversation_empty')}</p>
      </div>
    );
  }

  return (
    <div className="group-learning-block-content">
      <div className="group-learning-block-content__header">
        <div className="group-learning-block-content__tag-row">
          <div className="group-learning-block-content__tag-group">
            <RadarTypeTag type="group-learning-alert" />
            <TooltipCustom placement="bottom-start" title={t('chatbot:radar_detail_group_kc_tooltip')}>
              <span>
                <Icon color="grey-03" size="default" name="info-circle" />
              </span>
            </TooltipCustom>
          </div>
          <SeverityBadge severity={alert.severity} />
        </div>
        <div className="group-learning-block-content__kc-info">
          <h3 className="group-learning-block-content__kc-statement">{detail.kcStatement}</h3>
          {locationItems.length > 0 && (
            <ul className="group-learning-block-content__locations">
              {locationItems.map((loc) => (
                <li className="group-learning-block-content__location" key={loc}>
                  {loc}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className="group-learning-block-content__percentage-banner">
        <p>{t('chatbot:radar_detail_group_percentage_blocked', { percentage: Math.round(detail.percentage * 100) })}</p>
      </div>
      <div className="group-learning-block-content__students-section">
        <StatCard
          iconType="students"
          iconVariant="group"
          isExpandable
          isExpanded={isStudentsExpanded}
          label={t('chatbot:radar_detail_group_affected_students_count')}
          onToggle={handleToggleStudents}
          value={`${detail.affectedCount}/${detail.totalEnrolled}`}
        >
          <ul className="group-learning-block-content__severity-groups">
            {studentsBySeverity.map((group) => (
              <li className="group-learning-block-content__severity-group" key={group.severity}>
                <SeverityBadge orientation="horizontal" severity={group.severity} />
                <ul className="group-learning-block-content__student-list">
                  {group.students.map((student) => (
                    <li className="group-learning-block-content__student-item" key={student.studentId}>
                      <div className="group-learning-block-content__student-avatar">
                        {student.avatar ? (
                          <img
                            alt={`${student.studentName?.trim() ?? ''} ${student.studentLastName?.trim() ?? ''}`}
                            className="group-learning-block-content__student-avatar-img"
                            src={student.avatar}
                          />
                        ) : (
                          getInitials(student.studentName, student.studentLastName)
                        )}
                      </div>
                      <div className="group-learning-block-content__student-info">
                        <span className="group-learning-block-content__student-name">
                          {student.studentName?.trim()} {student.studentLastName?.trim()}
                        </span>
                        <span className="group-learning-block-content__student-correct">
                          <span className="group-learning-block-content__student-correct-count">
                            {student.correctCount}/{student.attemptCount}
                          </span>{' '}
                          <span className="group-learning-block-content__student-correct-label">{t('chatbot:radar_detail_group_student_correct')}</span>
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </StatCard>
      </div>
    </div>
  );
}

export default GroupLearningBlockContent;
